<script lang="ts">
  /**
   * A board on a phone (ma-inzp, plans/mobile-v2.md §3.6): canvas editing is
   * desktop-only, so the page shows a banner with a way into a read-only canvas,
   * the board's projects flattened in canvas reading order, and its text labels
   * as notes. Presentational: the page owns the canvas overlay.
   */
  import type { BoardItem, Project } from '../../lib/types';
  import { buildBoardList } from '../../lib/boardList';
  import DesktopOnlyNotice from '../DesktopOnlyNotice.svelte';
  import MobileBoardProjectRow from './MobileBoardProjectRow.svelte';

  interface Props {
    items: BoardItem[];
    projects: Project[];
    onViewCanvas: () => void;
  }

  let { items, projects, onViewCanvas }: Props = $props();

  let list = $derived(buildBoardList(items, projects));
  let canvasHasItems = $derived(items.length > 0);
</script>

<DesktopOnlyNotice
  feature="Canvas editing"
  variant="banner"
  actions={canvasHasItems ? [{ label: 'View canvas (read-only)', onSelect: onViewCanvas }] : []}
>
  {#snippet fallback()}
    {#if list.projects.length === 0 && list.notes.length === 0}
      <p class="empty">
        Nothing on this board yet. Open it on a larger screen to add projects and notes.
      </p>
    {/if}

    {#if list.projects.length > 0}
      <h2 class="section-h">
        Projects on this board <span class="count">{list.projects.length}</span>
      </h2>
      <ul class="rows" aria-label="Projects on this board">
        {#each list.projects as { item, project } (item.id)}
          <li><MobileBoardProjectRow {project} /></li>
        {/each}
      </ul>
    {/if}

    {#if list.notes.length > 0}
      <h2 class="section-h">Notes <span class="count">{list.notes.length}</span></h2>
      <ul class="notes" aria-label="Notes">
        {#each list.notes as note (note.id)}
          <li class="note-card">{note.content}</li>
        {/each}
      </ul>
    {/if}
  {/snippet}
</DesktopOnlyNotice>

<style>
  .section-h {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 16px 16px 8px;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
  }

  .count {
    font-weight: 500;
    color: #9ca3af;
  }

  .rows {
    margin: 0 12px;
    padding: 0;
    list-style: none;
    background: #fff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  }

  .rows li + li {
    border-top: 1px solid #f3f4f6;
  }

  .notes {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0 12px;
    padding: 0;
    list-style: none;
  }

  .note-card {
    padding: 12px 14px;
    border: 1px solid #fde68a;
    border-radius: 12px;
    background: #fefce8;
    font-size: 15px;
    line-height: 1.4;
    color: #1f2937;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .empty {
    margin: 24px 16px;
    text-align: center;
    font-size: 14px;
    color: #6b7280;
  }
</style>
